import { aiProcessRunsApi, applicationChangeApi, guidelinesApi } from 'app/services/api';
import _ from 'lodash';

import { AI_PROCESS_RUNS_ACTION_TYPE, LoadStatus } from 'constants/aiProcessRuns';
import { HTTP_RESPONSE_STATUS_CODES, SKIP_FORBIDDEN_RESPONSE_CONFIG } from 'constants/http';
import { LoanChangesHistoryState } from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';

import {
  selectFiltersForOwner,
  selectRunCitationsStatus,
  selectStepBlobStatus,
  selectStepCitationsStatus,
  selectStepMessagesStatus,
} from '../selectors';

export const loadRunsForOwnerAction = catchAsyncActionError(
  ({ ownerKind, ownerId }) =>
    async (dispatch, getState) => {
      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUNS,
        payload: { ownerId },
      });

      const filters = selectFiltersForOwner(ownerId)(getState());
      const { data } = await aiProcessRunsApi.getRunsForOwner({
        ownerKind,
        ownerId,
        ...filters,
      });

      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUNS_SUCCESS,
        payload: { ownerId, items: data?.items ?? [], totalCount: data?.totalCount ?? 0 },
      });
    },
);

export const setRunsFilterAction = ({ ownerId, partial }) => ({
  type: AI_PROCESS_RUNS_ACTION_TYPE.SET_RUNS_FILTER,
  payload: { ownerId, partial },
});

export const setRunsPageAction = ({ ownerId, skip }) => ({
  type: AI_PROCESS_RUNS_ACTION_TYPE.SET_RUNS_PAGE,
  payload: { ownerId, skip },
});

export const loadProcessesUsedByOwnerAction = catchAsyncActionError(
  ({ ownerKind, ownerId }) =>
    async dispatch => {
      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_PROCESSES_USED,
        payload: { ownerId },
      });

      const { data } = await aiProcessRunsApi.getProcessesUsedByOwner({ ownerKind, ownerId });

      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_PROCESSES_USED_SUCCESS,
        payload: { ownerId, items: data ?? [] },
      });
    },
);

export const loadRunDetailAction = catchAsyncActionError(({ executionId }) => async dispatch => {
  dispatch({
    type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUN_DETAIL,
    payload: { executionId },
  });

  const [{ data: run }, { data: steps }] = await Promise.all([
    aiProcessRunsApi.getRunDetail({ executionId }),
    aiProcessRunsApi.getRunSteps({ executionId }),
  ]);

  return dispatch({
    type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUN_DETAIL_SUCCESS,
    payload: { executionId, run, steps: steps ?? [] },
  });
});

export const loadStepMessagesAction = catchAsyncActionError(
  ({ stepExecutionId, force }) =>
    async (dispatch, getState) => {
      const currentStatus = selectStepMessagesStatus(stepExecutionId)(getState());

      // DEV-3480: skip both READY (already loaded) and LOADING (in flight) so
      // the drawer's run-wide eager-load of every step deduplicates against the
      // selected-step load and against itself instead of racing a second fetch.
      const isAlreadyLoadingOrLoaded =
        currentStatus === LoadStatus.READY || currentStatus === LoadStatus.LOADING;

      if (!force && isAlreadyLoadingOrLoaded) {
        return null;
      }

      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_MESSAGES,
        payload: { stepExecutionId },
      });

      const { data } = await aiProcessRunsApi.getStepMessages({ stepExecutionId });

      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_MESSAGES_SUCCESS,
        payload: { stepExecutionId, messages: data ?? [] },
      });
    },
);

const isLoadingOrLoaded = status => status === LoadStatus.READY || status === LoadStatus.LOADING;

// LEN-213: citation loads never toast. A failure (403 for a viewer without access,
// a network blip) settles the entry to ERROR and every chip / card for it renders a
// compact "citation unavailable" state instead of a page-level "No permissions" toast
// per citing step.
export const loadStepCitationsAction =
  ({ stepExecutionId, force }) =>
  async (dispatch, getState) => {
    const currentStatus = selectStepCitationsStatus(stepExecutionId)(getState());

    // Mirror the messages gate: skip both READY (already loaded) and LOADING
    // (in flight) so the run-wide eager-load and the selected-step load
    // deduplicate against each other instead of racing a second fetch.
    if (!force && isLoadingOrLoaded(currentStatus)) {
      return null;
    }

    dispatch({
      type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_CITATIONS,
      payload: { stepExecutionId },
    });

    try {
      // A step with no citations answers 404, which the API maps to a resolved
      // empty response — so a missing-citation step settles to READY with an
      // empty list rather than an error.
      const { data } = await aiProcessRunsApi.getStepCitations({ stepExecutionId });

      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_CITATIONS_SUCCESS,
        payload: { stepExecutionId, citations: Array.isArray(data) ? data : [] },
      });
    } catch {
      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_CITATIONS_FAILURE,
        payload: { stepExecutionId },
      });
    }
  };

// LEN-213: the run-wide citation list (step outputs + review-form values) the AI
// Activity chips resolve against. Silent on failure for the same reason as above.
export const loadRunCitationsAction =
  ({ executionId, force }) =>
  async (dispatch, getState) => {
    const currentStatus = selectRunCitationsStatus(executionId)(getState());

    if (!force && isLoadingOrLoaded(currentStatus)) {
      return null;
    }

    dispatch({
      type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUN_CITATIONS,
      payload: { executionId },
    });

    try {
      const { data } = await aiProcessRunsApi.getRunCitations({ executionId });

      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUN_CITATIONS_SUCCESS,
        payload: { executionId, citations: Array.isArray(data) ? data : [] },
      });
    } catch {
      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_RUN_CITATIONS_FAILURE,
        payload: { executionId },
      });
    }
  };

export const loadStepBlobAction = catchAsyncActionError(
  ({ stepExecutionId, kind, force }) =>
    async (dispatch, getState) => {
      const currentStatus = selectStepBlobStatus(stepExecutionId, kind)(getState());

      if (!force && currentStatus === LoadStatus.READY) {
        return null;
      }

      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_BLOB,
        payload: { stepExecutionId, kind },
      });

      const response = await aiProcessRunsApi.getStepBlob({ stepExecutionId, kind });
      const isEmpty = response.status === HTTP_RESPONSE_STATUS_CODES.NO_CONTENT;

      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_BLOB_SUCCESS,
        payload: {
          stepExecutionId,
          kind,
          blob: isEmpty ? null : (response.data ?? null),
        },
      });
    },
);

export const loadStepPendingToolCallAction = catchAsyncActionError(
  ({ stepExecutionId }) =>
    async dispatch => {
      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_PENDING_TOOL_CALL,
        payload: { stepExecutionId },
      });

      const response = await aiProcessRunsApi.getStepPendingToolCall({ stepExecutionId });
      const isEmpty = response.status === HTTP_RESPONSE_STATUS_CODES.NO_CONTENT;

      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_PENDING_TOOL_CALL_SUCCESS,
        payload: {
          stepExecutionId,
          pendingToolCall: isEmpty ? null : (response.data ?? null),
        },
      });
    },
);

export const setStepPendingToolCallAction = ({ stepExecutionId, pendingToolCall }) => ({
  type: AI_PROCESS_RUNS_ACTION_TYPE.SET_STEP_PENDING_TOOL_CALL,
  payload: { stepExecutionId, pendingToolCall },
});

// LEN-41: flatten the changes/history response ({ entityPrimaryKey, properties: [...] }[])
// into the per-property rows normalizeLoanChangesHistoryTableData expects — the same
// shape the loan-editor Pending Changes drawer feeds it (camelCased propertyName).
const flattenStagedLoanChanges = data =>
  (data ?? []).reduce((acc, { properties, ...rest }) => {
    (properties ?? []).forEach(prop => {
      acc.push({ ...prop, ...rest, propertyName: _.camelCase(prop.propertyName) });
    });

    return acc;
  }, []);

// LEN-41: fetch the loan's currently-STAGED (pending, not-yet-saved) changes so the
// commit_loan_changes confirmation can show the reviewer exactly what will be
// committed. createdByUserId is null on purpose: the AI process is the author of the
// staged drafts, and the loan-editor default filters to the current user, which would
// hide the AI's changes and make the gate look empty. A subtree-scoped TPO broker is
// not authorized here and legitimately 403s — degrade to an empty list, not a toast.
// Returns the flattened rows; the caller normalizes + renders them (no shared slice
// write, so the loan-editor's own pending-changes state is untouched).
export const loadStepStagedLoanChangesAction = catchAsyncActionError(
  ({ applicationId }) =>
    async () => {
      const { data } = await applicationChangeApi.getApplicationChangesHistory({
        applicationId,
        historyState: LoanChangesHistoryState.PENDING,
        createdByUserId: null,
        skipForbiddenError: true,
      });

      return flattenStagedLoanChanges(data);
    },
);

export const loadStepPendingQuestionAction = catchAsyncActionError(
  ({ stepExecutionId }) =>
    async dispatch => {
      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_PENDING_QUESTION,
        payload: { stepExecutionId },
      });

      const response = await aiProcessRunsApi.getStepPendingQuestion({ stepExecutionId });
      const isEmpty = response.status === HTTP_RESPONSE_STATUS_CODES.NO_CONTENT;

      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_PENDING_QUESTION_SUCCESS,
        payload: {
          stepExecutionId,
          pendingQuestion: isEmpty ? null : (response.data ?? null),
        },
      });
    },
);

export const setStepPendingQuestionAction = ({ stepExecutionId, pendingQuestion }) => ({
  type: AI_PROCESS_RUNS_ACTION_TYPE.SET_STEP_PENDING_QUESTION,
  payload: { stepExecutionId, pendingQuestion },
});

export const startAiProcessExecutionAction = catchAsyncActionError(
  ({ processId, ownerId, triggerSource = 'Manual', subjectCertificationIds = null }) =>
    async dispatch => {
      const { data } = await aiProcessRunsApi.startExecution({
        processId,
        entityId: ownerId,
        triggerSource,
        subjectCertificationIds,
      });

      if (data) {
        dispatch({
          type: AI_PROCESS_RUNS_ACTION_TYPE.EXECUTION_UPSERT,
          payload: { execution: data, ownerId },
        });
      }

      return data;
    },
);

export const confirmStepExecutionAction = catchAsyncActionError(
  ({
    stepExecutionId,
    decision,
    reason = null,
    selectedSlaGroupId = null,
    conditionDecisions = null,
  }) =>
    async dispatch => {
      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.CONFIRM_STEP_EXECUTION,
        payload: { stepExecutionId },
      });

      try {
        await aiProcessRunsApi.confirmStepExecution({
          stepExecutionId,
          decision,
          reason,
          selectedSlaGroupId,
          conditionDecisions,
        });
      } catch (error) {
        dispatch({
          type: AI_PROCESS_RUNS_ACTION_TYPE.CONFIRM_STEP_EXECUTION_FAILURE,
          payload: { stepExecutionId },
        });

        throw error;
      }

      // The actual status transition arrives via SignalR (step_started/step_completed
      // when the run resumes). We only clear the in-flight flag here.
      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.CONFIRM_STEP_EXECUTION_SUCCESS,
        payload: { stepExecutionId },
      });
    },
);

export const answerStepQuestionAction = catchAsyncActionError(
  ({ stepExecutionId, answersJson = null, dismissed = false }) =>
    async dispatch => {
      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.ANSWER_STEP_QUESTION,
        payload: { stepExecutionId },
      });

      try {
        await aiProcessRunsApi.answerStepExecution({ stepExecutionId, answersJson, dismissed });
      } catch (error) {
        dispatch({
          type: AI_PROCESS_RUNS_ACTION_TYPE.ANSWER_STEP_QUESTION_FAILURE,
          payload: { stepExecutionId },
        });

        throw error;
      }

      // The actual status transition arrives via SignalR (step_started/step_completed
      // when the run resumes). We only clear the in-flight flag here.
      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.ANSWER_STEP_QUESTION_SUCCESS,
        payload: { stepExecutionId },
      });
    },
);

export const retryStepExecutionAction = catchAsyncActionError(
  ({ stepExecutionId }) =>
    async dispatch => {
      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.RETRY_STEP_EXECUTION,
        payload: { stepExecutionId },
      });

      try {
        await aiProcessRunsApi.retryStepExecution({ stepExecutionId });
      } catch (error) {
        dispatch({
          type: AI_PROCESS_RUNS_ACTION_TYPE.RETRY_STEP_EXECUTION_FAILURE,
          payload: { stepExecutionId },
        });

        throw error;
      }

      // The step re-enters the pipeline server-side; the Failed→Pending→Running
      // transition arrives via SignalR. We only clear the in-flight flag here.
      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.RETRY_STEP_EXECUTION_SUCCESS,
        payload: { stepExecutionId },
      });
    },
);

export const retryStepFromHereExecutionAction = catchAsyncActionError(
  ({ stepExecutionId }) =>
    async dispatch => {
      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.RETRY_STEP_FROM_HERE_EXECUTION,
        payload: { stepExecutionId },
      });

      try {
        await aiProcessRunsApi.retryStepFromHereExecution({ stepExecutionId });
      } catch (error) {
        dispatch({
          type: AI_PROCESS_RUNS_ACTION_TYPE.RETRY_STEP_FROM_HERE_EXECUTION_FAILURE,
          payload: { stepExecutionId },
        });

        throw error;
      }

      // The failed step + downstream tail re-enter the pipeline server-side; the
      // status transitions arrive via SignalR. We only clear the in-flight flag here.
      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.RETRY_STEP_FROM_HERE_EXECUTION_SUCCESS,
        payload: { stepExecutionId },
      });
    },
);

export const selectExecutionAction = ({ ownerId, executionId }) => ({
  type: AI_PROCESS_RUNS_ACTION_TYPE.SELECT_EXECUTION,
  payload: { ownerId, executionId },
});

export const executionUpsertAction = ({ execution, ownerId }) => ({
  type: AI_PROCESS_RUNS_ACTION_TYPE.EXECUTION_UPSERT,
  payload: { execution, ownerId },
});

export const stepUpsertAction = ({ step }) => ({
  type: AI_PROCESS_RUNS_ACTION_TYPE.STEP_UPSERT,
  payload: { step },
});

export const messageAppendAction = ({ stepExecutionId, message }) => ({
  type: AI_PROCESS_RUNS_ACTION_TYPE.MESSAGE_APPEND,
  payload: { stepExecutionId, message },
});

export const loadExecutionFormAction = catchAsyncActionError(
  ({ executionId, force = false }) =>
    async dispatch => {
      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_FORM,
        payload: { executionId },
      });

      const response = await aiProcessRunsApi.getExecutionForm({ executionId });
      const isEmpty = response.status === HTTP_RESPONSE_STATUS_CODES.NO_CONTENT;

      return dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_FORM_SUCCESS,
        payload: { executionId, force, form: isEmpty ? null : (response.data ?? null) },
      });
    },
);

export const formFieldSetAction = ({ executionId, event }) => ({
  type: AI_PROCESS_RUNS_ACTION_TYPE.FORM_FIELD_SET,
  payload: { executionId, event },
});

export const formStatusChangedAction = ({ executionId, status }) => ({
  type: AI_PROCESS_RUNS_ACTION_TYPE.FORM_STATUS_CHANGED,
  payload: { executionId, status },
});

export const reviewFormFieldAction = catchAsyncActionError(
  ({ formId, fieldKey, reviewState, overriddenValue = null }) =>
    async dispatch => {
      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.REVIEW_FORM_FIELD,
        payload: { formId },
      });

      try {
        const { data } = await aiProcessRunsApi.reviewFormField({
          formId,
          fieldKey,
          reviewState,
          overriddenValue,
        });

        if (data) {
          dispatch({
            type: AI_PROCESS_RUNS_ACTION_TYPE.FORM_UPSERT,
            payload: { form: data },
          });
        }

        dispatch({
          type: AI_PROCESS_RUNS_ACTION_TYPE.REVIEW_FORM_FIELD_SUCCESS,
          payload: { formId },
        });

        return data;
      } catch (error) {
        dispatch({
          type: AI_PROCESS_RUNS_ACTION_TYPE.REVIEW_FORM_FIELD_FAILURE,
          payload: { formId },
        });

        throw error;
      }
    },
);

export const reviewFormFieldsAction = catchAsyncActionError(
  ({ formId, reviews }) =>
    async dispatch => {
      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.REVIEW_FORM_FIELDS,
        payload: { formId },
      });

      try {
        const { data } = await aiProcessRunsApi.reviewFormFields({ formId, reviews });

        if (data) {
          dispatch({
            type: AI_PROCESS_RUNS_ACTION_TYPE.FORM_UPSERT,
            payload: { form: data },
          });
        }

        dispatch({
          type: AI_PROCESS_RUNS_ACTION_TYPE.REVIEW_FORM_FIELDS_SUCCESS,
          payload: { formId },
        });

        return data;
      } catch (error) {
        dispatch({
          type: AI_PROCESS_RUNS_ACTION_TYPE.REVIEW_FORM_FIELDS_FAILURE,
          payload: { formId },
        });

        throw error;
      }
    },
);

export const transitionFormAction = catchAsyncActionError(
  ({ formId, action }) =>
    async dispatch => {
      dispatch({
        type: AI_PROCESS_RUNS_ACTION_TYPE.TRANSITION_FORM,
        payload: { formId },
      });

      try {
        const { data } = await aiProcessRunsApi.transitionForm({ formId, action });

        if (data) {
          dispatch({
            type: AI_PROCESS_RUNS_ACTION_TYPE.FORM_UPSERT,
            payload: { form: data },
          });
        }

        dispatch({
          type: AI_PROCESS_RUNS_ACTION_TYPE.TRANSITION_FORM_SUCCESS,
          payload: { formId },
        });

        return data;
      } catch (error) {
        dispatch({
          type: AI_PROCESS_RUNS_ACTION_TYPE.TRANSITION_FORM_FAILURE,
          payload: { formId },
        });

        throw error;
      }
    },
);

export const setStepGuidelineFullViewAction = citation => ({
  type: AI_PROCESS_RUNS_ACTION_TYPE.SET_STEP_GUIDELINE_FULL_VIEW,
  payload: { citation },
});

const cacheStepGuidelineContentAction = ({ id, content }) => ({
  type: AI_PROCESS_RUNS_ACTION_TYPE.CACHE_STEP_GUIDELINE_CONTENT,
  payload: { id, content },
});

const GUIDELINE_CITATION_GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Reuses the shared guideline-section lookup (the same one AI chat's "Read Full
// Section" calls — it is not chat-scoped): a real citation carries a source +
// section to resolve, while a bare/loading id falls back to a by-id lookup.
// Content is cached by citationId so re-opening a section never refetches.
export const fetchStepGuidelineContentAction =
  ({ citationId, sectionId, sourceId, sectionNumber }) =>
  async (dispatch, getState) => {
    const cached = getState().aiProcessRuns.guidelineCache[citationId];

    if (cached) {
      return cached;
    }

    try {
      const isGuid = _.isString(citationId) && GUIDELINE_CITATION_GUID_REGEX.test(citationId);
      const byIdTarget = sectionId ?? (isGuid ? citationId : null);

      // LEN-213: a resolved run citation carries its section id; prefer it. A 403
      // stays inline — the drawer falls back to the citation's excerpt.
      const { data } =
        !_.isNil(byIdTarget) || !sourceId || !sectionNumber
          ? await guidelinesApi.getSectionById(
              byIdTarget ?? citationId,
              SKIP_FORBIDDEN_RESPONSE_CONFIG,
            )
          : await guidelinesApi.lookupSection(
              { sourceId, sectionNumber },
              SKIP_FORBIDDEN_RESPONSE_CONFIG,
            );

      dispatch(cacheStepGuidelineContentAction({ id: citationId, content: data.content }));

      return data.content;
    } catch (error) {
      dispatch({ type: AI_PROCESS_RUNS_ACTION_TYPE.LOAD_STEP_GUIDELINE_CONTENT_FAILURE });

      return catchAsyncActionError(error);
    }
  };
